import { AVAILABLE_SERVICE, CONSULTATION_LANDING_STEP, Module, Step, Trace } from '@abiglobalhealth/go-types'
import * as Sentry from '@sentry/browser'
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { getInitialModule } from '@/api/modules'
import { useGeneralErrors } from '@/hooks/useGeneralErrors'
import { useLoading } from '@/hooks/useLoading'
import { useUser } from '@/hooks/useUser'
import { getInitialStep } from '@/utils/step'
import { formatTrace } from '@/utils/traces'

export interface JourneyContextProps {
  currentStep?: Step
  setCurrentStep: Dispatch<SetStateAction<Step | undefined>>

  currentModule?: Module
  setCurrentModule: Dispatch<SetStateAction<Module | undefined>>

  traces: Trace[]
  setTraces: Dispatch<SetStateAction<Trace[]>>

  initialized: boolean

  reminderSet: boolean
  setReminderSet: Dispatch<SetStateAction<boolean>>
}

export const JourneyContext = createContext({} as JourneyContextProps)

export const JourneyProvider = ({ children }: PropsWithChildren) => {
  const [currentStep, setCurrentStep] = useState<JourneyContextProps['currentStep']>()
  const [currentModule, setCurrentModule] = useState<JourneyContextProps['currentModule']>()
  const [traces, setTraces] = useState<JourneyContextProps['traces']>([])
  const [initialized, setInitialized] = useState(false)
  const [reminderSet, setReminderSet] = useState(false)

  const { clearGeneralErrors } = useGeneralErrors()
  const { showLoading, hideLoading } = useLoading()
  const { user, updateUser } = useUser()
  const { userId } = useParams()

  const initializeJourney = async () => {
    showLoading()

    try {
      const queryParams = new URLSearchParams(window.location.search)

      const data = await getInitialModule({
        uniqueId: user?.data?.uniqueId || undefined,
        userId: userId || undefined,
        language: user?.data?.language,
        country: user?.data?.country,
        serviceType: queryParams.get('q') as AVAILABLE_SERVICE
      })

      if (!data?.module) throw new Error('No module found')
      if (!data?.user) throw new Error('No user found')

      updateUser(data.user)

      setCurrentModule(data.module)

      if ('steps' in data.module && 'initialSteps' in data.module && data.module.initialSteps.length) {
        const currentStep = getInitialStep({ module: data.module, user: data.user })
        setCurrentStep(currentStep)
        setTraces([formatTrace(data.module.id, currentStep.id)])
      } else {
        setTraces([formatTrace(data.module.id, CONSULTATION_LANDING_STEP.id)])
      }

      setInitialized(true)
    } finally {
      hideLoading()
    }
  }

  useEffect(() => {
    if (!currentStep) return

    localStorage.setItem('currentStep', JSON.stringify(currentStep))

    Sentry.setContext('Current step', {
      id: currentStep?.id,
      name: currentStep?.name
    })
  }, [currentStep])

  useEffect(() => {
    if (!currentModule) return

    localStorage.setItem('currentModule', JSON.stringify(currentModule))

    Sentry.setContext('Current module', {
      id: currentModule?.id,
      name: currentModule?.name,
      type: currentModule?.type
    })
  }, [currentModule])

  useEffect(() => {
    clearGeneralErrors()

    if (!traces) return

    localStorage.setItem('traces', JSON.stringify(traces))
  }, [traces])

  useEffect(() => {
    if (user && !initialized) initializeJourney()
  }, [user])

  const value = {
    currentStep,
    setCurrentStep,
    currentModule,
    setCurrentModule,
    traces,
    setTraces,
    initialized,
    reminderSet,
    setReminderSet
  }

  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>
}
